import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  UsersIcon,
  BanknotesIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';
import adminApi, { Metrics } from '../../api/admin';
import { useLanguage } from '../../contexts/LanguageContext';

const AdminDashboard: React.FC = () => {
  const { t } = useLanguage();
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminApi
      .getMetrics()
      .then(setMetrics)
      .catch((e) => setError(e?.message ?? 'Failed to load metrics'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-slate-500">{t.common.loading}</div>;
  if (error) return <div className="text-red-600">{error}</div>;
  if (!metrics) return null;

  const cards = [
    {
      label: t.admin?.dashboard?.totalSubscribers ?? 'Abonnés actifs',
      value: metrics.totalSubscribers,
      icon: UsersIcon,
      tone: 'bg-indigo-50 text-indigo-700',
    },
    {
      label: t.admin?.dashboard?.mrr ?? 'Revenu mensuel récurrent',
      value: `${metrics.monthlyRecurringRevenue.toFixed(3)} TND`,
      icon: BanknotesIcon,
      tone: 'bg-green-50 text-green-700',
    },
    {
      label: t.admin?.dashboard?.pendingInvoices ?? 'Factures en attente',
      value: metrics.invoicesPendingThisMonth,
      icon: ClockIcon,
      tone: 'bg-amber-50 text-amber-700',
    },
    {
      label: t.admin?.dashboard?.planChanges ?? 'Changements de plan (30 j)',
      value: metrics.planChangesLast30Days,
      icon: ArrowTrendingUpIcon,
      tone: 'bg-purple-50 text-purple-700',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">
          {t.admin?.dashboard?.title ?? 'Tableau de bord administration'}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {t.admin?.dashboard?.subtitle ?? 'Vue d\'ensemble de la plateforme GreenBill.'}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <div className={`inline-flex p-2 rounded-lg ${c.tone}`}>
              <c.icon className="h-5 w-5" />
            </div>
            <div className="mt-4 text-2xl font-bold text-slate-900">{c.value}</div>
            <div className="text-sm text-slate-500 mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">
          {t.admin?.dashboard?.byPlan ?? 'Répartition par plan'}
        </h2>
        <div className="space-y-3">
          {Object.entries(metrics.subscribersByPlan).map(([plan, count]) => {
            const total = Math.max(metrics.totalSubscribers, 1);
            const pct = Math.round((count / total) * 100);
            return (
              <div key={plan}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium text-slate-700">{plan}</span>
                  <span className="text-slate-500">
                    {count} ({pct}%)
                  </span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-indigo-500 rounded-full"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 pt-4 border-t border-slate-100">
          <Link
            to="/admin/subscribers"
            className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
          >
            {t.admin?.dashboard?.viewAll ?? 'Voir tous les abonnés →'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
